import React, { useState, useMemo, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Download, FileText, Search, ClipboardList, ScrollText, Users, Upload, Trash2, RefreshCw, Paperclip, X, AlertTriangle, CheckCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Vollständige QM-Dokumentenliste (NW_QM_03_Dokumentenliste_QM, Stand 02/2026)
// status: "available" = direkt aus dem CRM druckbar
//         "per_teilnehmer" = nur in der Teilnehmerakte druckbar (kontextbezogen)
//         "extern" = wird ausserhalb des CRM gepflegt (Word/Datei-Server)
const DOKUMENTE = [
  // ===== Formblätter (FB) =====
  { nr: "FB_TN_01", titel: "Checkliste Erstgespräch", bereich: "01_Teilnehmer_und_Aufnahme", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Pro Teilnehmer in der Teilnehmer-Akte → Tab Erstgespräch generierbar." },
  { nr: "FB_TN_02", titel: "Maßnahmevertrag", bereich: "01_Teilnehmer_und_Aufnahme", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Pro Teilnehmer in der Teilnehmer-Akte → Tab Vertrag generierbar (kursabhängige Inhalte und Preise)." },
  { nr: "FB_TN_02a", titel: "AGB Dickies Campus", bereich: "01_Teilnehmer_und_Aufnahme", va: "VA_03", version: "V01", stand: "02/2026", status: "available", endpoint: "/azav/agb-pdf", filename: "AGB_Dickies_Campus.pdf", hinweis: "Wird zusammen mit dem Maßnahmevertrag (FB_TN_02) ausgehändigt." },
  { nr: "FB_TN_03", titel: "Einverständnis Datenschutz", bereich: "01_Teilnehmer_und_Aufnahme", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Pro Teilnehmer in der Teilnehmer-Akte → Tab Vertrag generierbar (mit Empfangsbestätigungs-Seite)." },
  { nr: "FB_TN_04", titel: "Teilnehmerakte Checkliste", bereich: "01_Teilnehmer_und_Aufnahme", va: "VA_03", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_TN_05", titel: "Anwesenheitsliste", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Pro Teilnehmer in der Teilnehmer-Akte → Tab Anwesenheit als PDF speicherbar." },
  { nr: "FB_TN_06", titel: "Teilnahmebescheinigung", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Wird beim Kursabschluss pro Teilnehmer erzeugt." },
  { nr: "FB_TN_07", titel: "Praktikumsvertrag", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Pro Teilnehmer in der Teilnehmer-Akte → Tab Praktikum generierbar (3-Parteien-Vertrag mit Bildungsträger, Praktikumsbetrieb und Praktikant)." },
  { nr: "FB_TN_08", titel: "Laufzettel Praktikum", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", endpoint: null, hinweis: "Pro Teilnehmer in der Teilnehmer-Akte → Tab Praktikum generierbar." },
  { nr: "FB_TN_09", titel: "Praktikumsbewertung", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Pro Teilnehmer in der Teilnehmer-Akte → Tab Praktikum generierbar." },
  { nr: "FB_TN_10", titel: "Gesprächsprotokoll Teilnehmer", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_TN_11", titel: "Arbeitsunterlagen-Übersicht", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "available", endpoint: "/azav/arbeitsunterlagen-pdf", filename: "Arbeitsunterlagen_Uebersicht_AZAV.pdf" },
  { nr: "FB_TN_12", titel: "Schulungsplan Behandlungspflege LG1 & LG2", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "06/2026", status: "available", endpoint: "/kurse/lg/schulungsplan-pdf", filename: "FB_TN_12_Schulungsplan_LG1_LG2.pdf", hinweis: "Curriculum + Tagesplan für Pflegeassistent LG1 & LG2 (208 UE / 32 Tage). Wird der Agentur für Arbeit als Schulungsplan vorgelegt." },
  { nr: "FB_TN_13", titel: "Schulungsplan Pflegehilfskraft & Betreuungsassistent", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "06/2026", status: "available", endpoint: "/kurse/pfh/schulungsplan-pdf", filename: "FB_TN_13_Schulungsplan_PfH.pdf", hinweis: "Curriculum + Tagesplan für Pflegehilfskraft & Betreuungsassistent. Wird der Agentur für Arbeit als Schulungsplan vorgelegt." },
  { nr: "FB_TN_14", titel: "Schulungsplan Ambulanter Pflegeassistent", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "06/2026", status: "available", endpoint: "/kurse/ambulant/schulungsplan-pdf", filename: "FB_TN_14_Schulungsplan_Ambulanter_Pflegeassistent.pdf", hinweis: "Curriculum + Tagesplan für Ambulanten Pflegeassistent. Wird der Agentur für Arbeit als Schulungsplan vorgelegt." },
  { nr: "FB_TN_15", titel: "Schulungsplan Pflegefachassistenz Externenprüfung (NRW)", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "06/2026", status: "available", endpoint: "/kurse/pfa_extern_online/schulungsplan-pdf", filename: "FB_TN_15_Schulungsplan_PflegefachassistenzExtern_NRW.pdf", hinweis: "Curriculum + Tagesplan für die Online-Vorbereitung auf die Externenprüfung Pflegefachassistenz NRW (300 UE / 43 Kurstage, Blended Online). KEIN Praktikum erforderlich." },
  { nr: "FB_QM_01_PFA", titel: "Maßnahmekonzept Pflegefachassistenz Externenprüfung", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "06/2026", status: "available", endpoint: "/kurse/pfa_extern_online/massnahmekonzept-pdf", filename: "FB_QM_01_Massnahmekonzept_PflegefachassistenzExtern.pdf", hinweis: "Vollständiges AZAV-Maßnahmekonzept für die Beantragung bei der Fachkundigen Stelle (FKS). § 1–9 inkl. Distanzunterrichts-Konzept." },
  { nr: "FB_QM_02_PFA", titel: "Curriculum Pflegefachassistenz Externenprüfung", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "06/2026", status: "available", endpoint: "/kurse/pfa_extern_online/curriculum-pdf", filename: "FB_QM_02_Curriculum_PflegefachassistenzExtern.pdf", hinweis: "Kompakte Modulübersicht mit UE-Verteilung als Anlage zum Maßnahmekonzept." },

  { nr: "FB_EVAL_01", titel: "Teilnehmerbefragung", bereich: "Evaluation", va: "VA_01", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_EVAL_02", titel: "Verbleibsbefragung", bereich: "Evaluation", va: "VA_01", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_EVAL_04", titel: "Dozentenbefragung", bereich: "Evaluation", va: "VA_01", version: "V01", stand: "02/2026", status: "extern" },

  { nr: "FB_AUD_01", titel: "Auditplan", bereich: "Audit", va: "VA_02", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_AUD_02", titel: "Auditcheckliste AZAV", bereich: "Audit", va: "VA_02", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_AUD_03", titel: "Auditbericht (Vorlage)", bereich: "Audit", va: "VA_02", version: "V01", stand: "02/2026", status: "available", endpoint: "/azav/auditbericht-vorlage-pdf", filename: "Auditbericht_Vorlage.pdf" },
  { nr: "FB_AUD_04", titel: "Fragekatalog Internes Audit", bereich: "Audit", va: "VA_02", version: "V01", stand: "02/2026", status: "extern" },

  { nr: "FB_PERS_01", titel: "Schulungsnachweis", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_PERS_02", titel: "Einarbeitungsnachweis", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_PERS_10", titel: "Honorarvertrag", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },

  { nr: "FB_QM_01", titel: "AGB AZAV", bereich: "QM", va: "VA_05", version: "V01", stand: "02/2026", status: "upload-managed", endpoint: null, filename: "FB_QM_01_AGB_AZAV.pdf" },
  { nr: "FB_QM_02", titel: "Datenschutzkonzept", bereich: "QM", va: "VA_05", version: "V01", stand: "02/2026", status: "upload-managed", endpoint: null, filename: "FB_QM_02_Datenschutzkonzept.pdf" },
  { nr: "FB_QM_05", titel: "Unterschriftenkürzel-Verzeichnis", bereich: "QM", va: "VA_05", version: "V01", stand: "02/2026", status: "available", endpoint: "/azav/unterschriften-verzeichnis", filename: "Unterschriftenverzeichnis_AZAV.pdf" },
  { nr: "FB_QM_06", titel: "Kontrollmechanismus Online-Anwesenheit", bereich: "QM", va: "VA_03", version: "V01", stand: "02/2026", status: "available", endpoint: "/azav/kontrollmechanismus-pdf", filename: "Kontrollmechanismus_Online_Anwesenheit.pdf" },
  { nr: "FB_QM_03", titel: "Beschwerdeprotokoll", bereich: "QM", va: "VA_08", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "FB_QM_04", titel: "Besprechungsprotokoll", bereich: "QM", va: "VA_06", version: "V01", stand: "02/2026", status: "extern" },

  // ===== Nachweise (NW) =====
  { nr: "NW_QM_01", titel: "Leitbild", bereich: "QM-Grundlagen", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_QM_02", titel: "Fehlzeitenkonzept", bereich: "QM-Grundlagen", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_QM_03", titel: "Dokumentenliste QM (für Auditor)", bereich: "QM-Grundlagen", va: "VA_05", version: "V02", stand: "05/2026", status: "available", endpoint: "/azav/dokumentenliste-pdf", filename: "NW_QM_03_Dokumentenliste_QM_V02.pdf" },
  { nr: "NW_QM_04", titel: "Organigramm", bereich: "QM-Grundlagen", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_QM_05", titel: "Zusammenarbeit mit Dritten", bereich: "QM-Grundlagen", va: "VA_10", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_QM_06", titel: "Jahresziele und Kennzahlen", bereich: "Ziele", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_QM_07", titel: "Jahresziele Vergangenheit", bereich: "Ziele", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_QM_08", titel: "Auswertung Maßnahmen", bereich: "Ziele", va: "VA_01", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_QM_09", titel: "Beschwerdeübersicht", bereich: "QM", va: "VA_08", version: "V01", stand: "02/2026", status: "extern" },

  { nr: "NW_TN_01", titel: "Klassenbuch", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Im Bereich Klassenbuch verfügbar; pro Kurs/Teilnehmer als PDF exportierbar." },
  { nr: "NW_TN_02", titel: "Teilnehmerübersicht", bereich: "03_TN_Maßnahmen", va: "VA_03", version: "V01", stand: "02/2026", status: "per_teilnehmer", hinweis: "Im Bereich Teilnehmer per Excel-Export." },
  { nr: "NW_TN_03", titel: "Auswertung Teilnehmerbefragung", bereich: "Evaluation", va: "VA_01", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_TN_04", titel: "Verbleibsauswertung", bereich: "Evaluation", va: "VA_01", version: "V01", stand: "02/2026", status: "extern" },

  { nr: "NW_PERS_01", titel: "Funktionsbeschreibung QMB", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_PERS_02", titel: "Funktionsbeschreibung Verwaltung", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_PERS_03", titel: "Funktionszuordnung", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_PERS_04", titel: "Schulungsplan", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_PERS_05", titel: "Dozentenübersicht", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "NW_PERS_06", titel: "Qualifikationsmatrix Mitarbeiter & Dozenten", bereich: "04_PERS", va: "VA_07", version: "V01", stand: "02/2026", status: "extern" },

  // ===== Verfahrensanweisungen (VA) =====
  { nr: "VA_01", titel: "Evaluation", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_02", titel: "Internes Audit", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_03", titel: "Kernprozess Durchführung AZAV-Maßnahmen", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_04", titel: "Korrekturmaßnahmen", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_05", titel: "Lenkung von dokumentierten Informationen", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_06", titel: "Managementbewertung", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_07", titel: "Personal und Weiterbildung", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_08", titel: "Umgang mit Reklamationen", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_09", titel: "Vorbeugemaßnahmen", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_10", titel: "Zusammenarbeit mit Dritten", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_11", titel: "Durchführungsformen und digitale Maßnahmen", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
  { nr: "VA_12", titel: "Nutzung des CRM-Systems / Klassenbuch durch Dozenten", bereich: "Verfahren", va: "—", version: "V01", stand: "02/2026", status: "extern" },
];

const STATUS_BADGE = {
  available: { label: "Direkt druckbar", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  per_teilnehmer: { label: "Im Teilnehmer-Tab", className: "bg-blue-100 text-blue-800 border-blue-200" },
  per_kurs: { label: "Im Klassenbuch (pro Kurs)", className: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  extern: { label: "Extern (Word/Server)", className: "bg-stone-100 text-stone-700 border-stone-200" },
  uploaded: { label: "Hochgeladen", className: "bg-violet-100 text-violet-800 border-violet-200" },
  "upload-managed": { label: "QM-Upload verwaltet", className: "bg-amber-100 text-amber-800 border-amber-200" },
};
const FALLBACK_BADGE = { label: "Sonstiges", className: "bg-stone-100 text-stone-700 border-stone-200" };

const KATEGORIEN = [
  { key: "FB", label: "Formblätter", icon: ClipboardList, color: "text-emerald-600" },
  { key: "NW", label: "Nachweise", icon: FileText, color: "text-blue-600" },
  { key: "VA", label: "Verfahrensanweisungen", icon: ScrollText, color: "text-amber-600" },
];

const AZAVDokumente = () => {
  const { user } = useAuth();
  const isReadOnly = user?.role === "dozent";
  const [filter, setFilter] = useState("");
  const [activeKat, setActiveKat] = useState("FB");
  const [uploadedDocs, setUploadedDocs] = useState({});  // {nr: doc}
  const [uploadingNr, setUploadingNr] = useState(null);
  const [referencesMap, setReferencesMap] = useState({}); // {code: [ref, ...]}
  const [refsModalCode, setRefsModalCode] = useState(null);

  // === Bulk-Resign FB_TN_02 (Praktikum/Zeit-Korrektur 25.02.2026) ===
  const [resignBusy, setResignBusy] = useState(false);
  const [resignPreview, setResignPreview] = useState(null); // dry-run Ergebnis
  const [resignConfirmOpen, setResignConfirmOpen] = useState(false);
  const [resignResult, setResignResult] = useState(null);
  const [resignProgress, setResignProgress] = useState(null); // {done, total, currentBatch}

  const runResignDryRun = async () => {
    setResignBusy(true);
    setResignResult(null);
    try {
      const res = await axios.post(`${API}/teilnehmer/bulk-resign-mv`, { dry_run: true, limit: 500 });
      setResignPreview(res.data);
      toast.success(`Vorschau: ${res.data.resigned_count} Verträge würden regeneriert.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler bei der Vorschau");
    } finally {
      setResignBusy(false);
    }
  };

  const runResignReal = async () => {
    setResignBusy(true);
    setResignConfirmOpen(false);
    // Batch-Loop: 15 Verträge pro Request, damit Cloudflare-Timeout (120s) nicht auslöst.
    const BATCH = 15;
    const total = resignPreview?.resigned_count || 0;
    let done = 0;
    let cumulative = { resigned_count: 0, skipped_count: 0, resigned: [], errors: [] };
    setResignProgress({ done: 0, total, batchNo: 0 });
    try {
      // Sicherheits-Cap: maximal 20 Batches (=300 Verträge)
      for (let batchNo = 1; batchNo <= 20; batchNo++) {
        setResignProgress({ done, total, batchNo });
        const res = await axios.post(
          `${API}/teilnehmer/bulk-resign-mv`,
          { dry_run: false, limit: BATCH },
          { timeout: 180000 } // 3 Min Client-Timeout — Fallback falls Cloudflare doch früher zuschlägt
        );
        const batchCount = res.data.resigned_count || 0;
        cumulative.resigned_count += batchCount;
        cumulative.skipped_count += (res.data.skipped_count || 0);
        cumulative.resigned = cumulative.resigned.concat(res.data.resigned || []);
        cumulative.errors = cumulative.errors.concat(res.data.errors || []);
        done += batchCount;
        setResignProgress({ done, total: Math.max(total, done), batchNo });
        if (batchCount === 0) {
          // Nichts mehr zu tun
          break;
        }
        // kurze Pause, damit die DB atmen kann
        await new Promise((r) => setTimeout(r, 400));
      }
      setResignResult(cumulative);
      setResignPreview(null);
      setResignProgress(null);
      toast.success(`Fertig: ${cumulative.resigned_count} Verträge regeneriert (Original-Signatur bewahrt).`);
    } catch (err) {
      const partial = cumulative.resigned_count;
      setResignProgress(null);
      if (partial > 0) {
        setResignResult(cumulative);
        toast.error(`Teilweise fertig: ${partial} Verträge regeneriert, dann Abbruch — Rest bitte über "Jetzt regenerieren" erneut starten.`);
      } else {
        toast.error(err.response?.data?.detail || err.message || "Fehler beim Regenerieren");
      }
    } finally {
      setResignBusy(false);
    }
  };

  const fileInputRefs = useRef({});

  const loadReferences = async () => {
    try {
      const res = await axios.get(`${API}/qm/documents`);
      const refsMap = {};
      const uploadedFromQm = {};
      for (const d of res.data?.documents || []) {
        if (Array.isArray(d.referenz_anhaenge) && d.referenz_anhaenge.length > 0) {
          refsMap[d.code] = d.referenz_anhaenge;
        }
        // Falls in qm_documents ein Blanko-PDF hinterlegt ist (z.B. via Sync-Script),
        // mappen wir das auf das Legacy-Schema, damit der "Hochgeladen"-Status rendert.
        if (d.blanko_pdf_filename || d.blanko_pdf_size) {
          uploadedFromQm[d.code] = {
            nr: d.code,
            filename: d.blanko_pdf_filename || `${d.code}.pdf`,
            uploaded_at: d.blanko_pdf_uploaded_at || d.updated_at || new Date().toISOString(),
            file_size: d.blanko_pdf_size || 0,
            source: "qm_documents",
          };
        }
      }
      setReferencesMap(refsMap);
      // Merge: qm_documents-Daten dürfen Legacy-Status überschreiben (sind aktueller)
      setUploadedDocs((prev) => ({ ...prev, ...uploadedFromQm }));
    } catch (err) {
      // silent
    }
  };

  const downloadReference = async (code, refId, fallbackName) => {
    try {
      const res = await axios.get(`${API}/qm/documents/${code}/references/${refId}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = (fallbackName || `${code}_${refId}`).replace(/\.[^/.]+$/, "") + ".pdf";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Referenz konnte nicht geladen werden");
    }
  };

  const loadUploadedDocs = async () => {
    try {
      const res = await axios.get(`${API}/qm-dokumente`);
      const map = {};
      for (const d of res.data || []) {
        map[d.nr] = d;
      }
      setUploadedDocs(map);
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    loadUploadedDocs();
    loadReferences();
  }, []);

  const handleUploadClick = (nr) => {
    fileInputRefs.current[nr]?.click();
  };

  const handleFileSelected = async (e, doc) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // Reset
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Nur PDF-Dateien sind erlaubt.");
      return;
    }
    setUploadingNr(doc.nr);
    try {
      const fd = new FormData();
      fd.append("nr", doc.nr);
      fd.append("titel", doc.titel);
      fd.append("bereich", doc.bereich || "");
      fd.append("version", doc.version || "V01");
      fd.append("stand", doc.stand || "");
      fd.append("file", file);
      const res = await axios.post(`${API}/qm-dokumente/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedDocs(prev => ({ ...prev, [doc.nr]: res.data }));
      toast.success(`${doc.nr} hochgeladen`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingNr(null);
    }
  };

  const downloadUploaded = async (nr) => {
    // Probiert zuerst Legacy-Endpoint (qm_dokumente collection), fällt dann auf
    // den neuen Endpoint zurück (qm_documents collection) — wichtig nach Sync.
    try {
      const res = await axios.get(`${API}/qm-dokumente/${nr}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${nr}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      return;
    } catch (err) {
      // Fallback auf qm_documents
      try {
        const res = await axios.get(`${API}/qm/documents/${nr}/blanko-download`, { responseType: "blob" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const a = document.createElement("a");
        a.href = url;
        a.download = `${nr}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } catch (err2) {
        toast.error("Download fehlgeschlagen");
      }
    }
  };

  const deleteUploaded = async (nr) => {
    if (!window.confirm(`Hochgeladenes Dokument ${nr} löschen?`)) return;
    try {
      await axios.delete(`${API}/qm-dokumente/${nr}`);
      setUploadedDocs(prev => {
        const copy = { ...prev };
        delete copy[nr];
        return copy;
      });
      toast.success("Gelöscht");
    } catch (err) {
      toast.error("Fehler beim Löschen");
    }
  };

  const downloadPDF = async (endpoint, filename) => {
    if (!endpoint) {
      toast.info("Dieses Dokument wird ausserhalb des CRM gepflegt oder direkt im Teilnehmer-Tab erzeugt.");
      return;
    }
    try {
      toast.info("PDF wird generiert...");
      const res = await axios.get(`${API}${endpoint}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF heruntergeladen");
    } catch (err) {
      toast.error("Fehler beim Generieren des PDFs");
    }
  };

  const counts = useMemo(() => ({
    FB: DOKUMENTE.filter(d => d.nr.startsWith("FB_")).length,
    NW: DOKUMENTE.filter(d => d.nr.startsWith("NW_")).length,
    VA: DOKUMENTE.filter(d => d.nr.startsWith("VA_")).length,
    available: DOKUMENTE.filter(d => d.status === "available").length,
  }), []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return DOKUMENTE.filter(d => {
      if (!d.nr.startsWith(activeKat + "_") && !(activeKat === "VA" && d.nr.startsWith("VA_"))) return false;
      if (!q) return true;
      return (
        d.nr.toLowerCase().includes(q) ||
        d.titel.toLowerCase().includes(q) ||
        d.bereich.toLowerCase().includes(q)
      );
    });
  }, [filter, activeKat]);

  return (
    <div className="p-6 space-y-6" data-testid="azav-dokumente-page">
      {/* Auditor-Paket ZIP Hero */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-400" data-testid="azav-auditor-zip-card">
        <CardContent className="py-5">
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                <Download size={20} />
              </div>
              <div>
                <h3 className="font-bold text-stone-900 text-base">Auditor-Nachweispaket — ZIP-Download</h3>
                <p className="text-xs text-stone-600 mt-1 max-w-3xl leading-relaxed">
                  <b>DSGVO-konforme Trennung:</b> Zwei getrennte ZIPs — je einer pro Empfänger:in, damit jede:r nur die für sie:ihn bestimmten Nachweise (und keine fremden Personendaten) erhält. Erstellung dauert ca. 30–60 s pro ZIP.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Krasniqi ZIP */}
              <Button
                onClick={async () => {
                  const t = toast.loading("Krasniqi-ZIP wird erzeugt (30–60 s) …");
                  try {
                    const res = await axios.get(`${API}/azav/auditor-paket-zip?recipient=krasniqi`, {
                      responseType: 'blob',
                      timeout: 180000,
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
                    const a = document.createElement('a');
                    a.href = url;
                    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                    a.setAttribute('download', `Auditor-Paket_Krasniqi_${stamp}.zip`);
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.dismiss(t);
                    toast.success("Krasniqi-ZIP heruntergeladen");
                  } catch (err) {
                    toast.dismiss(t);
                    let detail = "Download fehlgeschlagen";
                    if (err.response?.data instanceof Blob) {
                      try { const txt = await err.response.data.text(); const j = JSON.parse(txt); detail = j.detail || detail; } catch (e) {}
                    } else if (err.response?.data?.detail) detail = err.response.data.detail;
                    else if (err.message) detail = err.message;
                    toast.error(detail);
                  }
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md whitespace-nowrap flex-1"
                data-testid="btn-download-zip-krasniqi"
              >
                <Download size={16} className="mr-2" /> ZIP für Krasniqi (FKS · P1–8)
              </Button>

              {/* Schmitz ZIP */}
              <Button
                onClick={async () => {
                  const t = toast.loading("Schmitz-ZIP wird erzeugt …");
                  try {
                    const res = await axios.get(`${API}/azav/auditor-paket-zip?recipient=schmitz`, {
                      responseType: 'blob',
                      timeout: 180000,
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
                    const a = document.createElement('a');
                    a.href = url;
                    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                    a.setAttribute('download', `Auditor-Paket_Schmitz_${stamp}.zip`);
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.dismiss(t);
                    toast.success("Schmitz-ZIP heruntergeladen");
                  } catch (err) {
                    toast.dismiss(t);
                    let detail = "Download fehlgeschlagen";
                    if (err.response?.data instanceof Blob) {
                      try { const txt = await err.response.data.text(); const j = JSON.parse(txt); detail = j.detail || detail; } catch (e) {}
                    } else if (err.response?.data?.detail) detail = err.response.data.detail;
                    else if (err.message) detail = err.message;
                    toast.error(detail);
                  }
                }}
                className="bg-teal-600 hover:bg-teal-700 text-white shadow-md whitespace-nowrap flex-1"
                data-testid="btn-download-zip-schmitz"
              >
                <Download size={16} className="mr-2" /> ZIP für Schmitz (APV-Leitung · P9–10)
              </Button>

              {/* Kombiniert (Legacy) */}
              <Button
                variant="outline"
                onClick={async () => {
                  const t = toast.loading("Kombiniertes ZIP wird erzeugt …");
                  try {
                    const res = await axios.get(`${API}/azav/auditor-paket-zip`, {
                      responseType: 'blob',
                      timeout: 180000,
                    });
                    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
                    const a = document.createElement('a');
                    a.href = url;
                    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                    a.setAttribute('download', `Auditor-Paket_Dickies-Campus_${stamp}.zip`);
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.dismiss(t);
                    toast.success("Kombiniertes ZIP heruntergeladen");
                  } catch (err) {
                    toast.dismiss(t);
                    toast.error(err.message || "Download fehlgeschlagen");
                  }
                }}
                className="border-stone-400 text-stone-700 hover:bg-stone-100 whitespace-nowrap"
                data-testid="btn-download-zip-kombiniert"
              >
                <Download size={16} className="mr-2" /> Kombiniertes ZIP (intern)
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Auditor-Export Hero-Card */}
      <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-300" data-testid="azav-auditor-export-card">
        <CardContent className="py-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-orange-500 text-white flex items-center justify-center">
                <ScrollText size={20} />
              </div>
              <div>
                <h3 className="font-bold text-stone-900 text-base">Aktualisierte QM-Dokumentenliste für den Auditor</h3>
                <p className="text-xs text-stone-600 mt-1 max-w-2xl leading-relaxed">
                  Offizielles <b>NW_QM_03</b>-PDF in Version <b>V02 (05/2026)</b> mit Änderungshistorie, vollständiger Dokumentenübersicht und Unterschriftsblock zur Freigabe. Neu aufgenommen: <b>FB_TN_12, FB_TN_13, FB_TN_14</b> (Stundenpläne).
                </p>
              </div>
            </div>
            <Button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/azav/dokumentenliste-pdf`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  a.setAttribute('download', `NW_QM_03_Dokumentenliste_V02_${new Date().toISOString().slice(0,10).replace(/-/g,'')}.pdf`);
                  document.body.appendChild(a); a.click(); a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('NW_QM_03 V02 für Auditor heruntergeladen');
                } catch (err) {
                  let detail = 'Fehler beim Erstellen des PDFs';
                  if (err.response?.data instanceof Blob) {
                    try { const t = await err.response.data.text(); const j = JSON.parse(t); detail = j.detail || detail; } catch (e) {}
                  } else if (err.response?.data?.detail) detail = err.response.data.detail;
                  toast.error(detail);
                }
              }}
              className="bg-orange-500 hover:bg-orange-600 text-white shadow-md whitespace-nowrap"
              data-testid="btn-download-nw-qm-03-auditor"
            >
              <Download size={16} className="mr-2" /> NW_QM_03 V02 herunterladen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Bulk-Resign FB_TN_02 (Korrektur „Praktikum" bei LG/Ambulanter + Unterrichtszeiten) */}
      <Card className="border-amber-300 bg-amber-50/40" data-testid="azav-bulk-resign-mv-card">
        <CardContent className="py-5">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0 w-11 h-11 rounded-full bg-amber-500 text-white flex items-center justify-center">
                <AlertTriangle size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-stone-900 text-base">Alt-Verträge FB_TN_02 auf neuen Text aktualisieren</h3>
                <p className="text-xs text-stone-700 mt-1 max-w-3xl leading-relaxed">
                  Bereits signierte Teilnehmerverträge werden mit dem korrigierten Text neu erzeugt:
                  <br/>• <b>LG1/LG2 &amp; Ambulanter PA</b>: „3-monatiges Praktikum"-Zusatz entfernt (kein Praktikum in diesen Maßnahmen)
                  <br/>• Kursabhängige <b>Unterrichtszeit</b> (LG 15:30, Ambulant/PHK 15:00) — statt fälschlich 15:15
                  <br/>• <b>§ 3 Maßnahmenort</b> präziser („Ein Praktikum ist nicht Bestandteil dieser Maßnahme")
                  <br/><br/>
                  <b>Rechtssicher:</b> Original-Signatur (aus <code>referenz_unterschrift_b64</code>) wird neu eingebrannt,
                  Original-Zeitstempel/IP/Token bleiben unverändert (eIDAS-Kette intakt). Zusätzlich werden
                  <code>resigned_at</code> + <code>resign_reason</code> für den Audit-Trail dokumentiert.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 w-full md:w-auto md:min-w-[220px]">
              <Button
                onClick={runResignDryRun}
                disabled={resignBusy}
                variant="outline"
                className="border-amber-500 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                data-testid="btn-resign-mv-dry-run"
              >
                <RefreshCw size={14} className={`mr-2 ${resignBusy ? 'animate-spin' : ''}`} /> 1. Vorschau (Dry-Run)
              </Button>
              <Button
                onClick={() => setResignConfirmOpen(true)}
                disabled={resignBusy || !resignPreview || resignPreview.resigned_count === 0}
                className="bg-amber-600 hover:bg-amber-700 text-white shadow-md whitespace-nowrap"
                data-testid="btn-resign-mv-real"
              >
                <CheckCircle size={14} className="mr-2" /> 2. Jetzt regenerieren
              </Button>
            </div>
          </div>

          {/* Dry-Run Preview */}
          {resignPreview && (
            <div className="mt-4 p-3 bg-white border border-amber-200 rounded-md" data-testid="resign-mv-preview">
              <div className="text-xs font-semibold text-stone-700 mb-2">
                Vorschau: <b>{resignPreview.resigned_count}</b> Verträge werden regeneriert
                {resignPreview.skipped_count > 0 && (
                  <span className="ml-2 text-stone-500">· {resignPreview.skipped_count} übersprungen (keine Referenz-Signatur)</span>
                )}
              </div>
              <div className="max-h-40 overflow-y-auto text-xs text-stone-600 space-y-0.5">
                {(resignPreview.resigned || []).slice(0, 30).map((r) => (
                  <div key={r.teilnehmer_id} className="flex justify-between border-b border-stone-100 py-1">
                    <span>{r.name}</span>
                    <span className="text-stone-400">Original signed: {new Date(r.signed_at).toLocaleDateString('de-DE')}</span>
                  </div>
                ))}
                {resignPreview.resigned && resignPreview.resigned.length > 30 && (
                  <div className="text-stone-400 pt-1">… und {resignPreview.resigned.length - 30} weitere</div>
                )}
              </div>
            </div>
          )}

          {/* Batch-Progress während echtem Lauf */}
          {resignProgress && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-300 rounded-md" data-testid="resign-mv-progress">
              <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                <RefreshCw size={16} className="animate-spin" />
                Regeneration läuft — Batch {resignProgress.batchNo} · {resignProgress.done} / {resignProgress.total} fertig
              </div>
              <div className="mt-2 h-2 bg-blue-100 rounded overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all"
                  style={{ width: `${Math.min(100, Math.round((resignProgress.done / Math.max(1, resignProgress.total)) * 100))}%` }}
                />
              </div>
              <div className="mt-1 text-[11px] text-blue-800">
                Bitte Tab nicht schließen — Batches à 15 Verträge, dazwischen kurze Pause.
              </div>
            </div>
          )}

          {/* Final Result */}
          {resignResult && (
            <div className="mt-4 p-3 bg-green-50 border border-green-300 rounded-md" data-testid="resign-mv-result">
              <div className="flex items-center gap-2 text-sm font-semibold text-green-900">
                <CheckCircle size={16} /> Fertig: <b>{resignResult.resigned_count}</b> Verträge regeneriert.
                {resignResult.errors?.length > 0 && (
                  <span className="text-red-700 ml-2">Fehler: {resignResult.errors.length}</span>
                )}
              </div>
              {resignResult.errors?.length > 0 && (
                <div className="mt-2 text-xs text-red-700 space-y-1">
                  {resignResult.errors.slice(0, 5).map((e, i) => (
                    <div key={i}>• TN {e.teilnehmer_id}: {e.reason}</div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bestätigungs-Dialog für den echten Lauf */}
      {resignConfirmOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setResignConfirmOpen(false)} data-testid="resign-mv-confirm-dialog">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3">
              <AlertTriangle className="text-amber-600" size={28} />
              <h3 className="font-bold text-lg text-stone-900">Verträge jetzt regenerieren?</h3>
            </div>
            <p className="text-sm text-stone-700 leading-relaxed">
              <b>{resignPreview?.resigned_count}</b> signierte Teilnehmerverträge werden mit dem
              neuen korrigierten Text neu erzeugt. Original-Signatur, Zeitstempel, IP und Token
              bleiben unverändert (rechtssicher, eIDAS-Kette intakt).
              <br/><br/>
              Die alten PDF-Dateien werden ersetzt. <b>Rückgängig ist der Vorgang nicht.</b>
            </p>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" onClick={() => setResignConfirmOpen(false)} data-testid="btn-resign-cancel">
                Abbrechen
              </Button>
              <Button onClick={runResignReal} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="btn-resign-confirm">
                Ja, jetzt regenerieren
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Manrope, sans-serif", color: "#6d665b" }}>
            AZAV-Dokumente
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            Vollständige QM-Dokumentenliste (NW_QM_03) — {DOKUMENTE.length} Dokumente, {counts.available} direkt druckbar
          </p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Nummer, Titel, Bereich…"
            className="pl-9"
            data-testid="azav-search-input"
          />
        </div>
      </div>

      {/* Kategorie-Tabs */}
      <div className="flex gap-2 border-b border-stone-200">
        {KATEGORIEN.map(({ key, label, icon: Icon, color }) => (
          <button
            key={key}
            onClick={() => setActiveKat(key)}
            data-testid={`azav-tab-${key.toLowerCase()}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeKat === key
                ? "border-orange-500 text-stone-900"
                : "border-transparent text-stone-500 hover:text-stone-700"
            }`}
          >
            <Icon size={16} className={color} />
            <span>{label}</span>
            <span className="text-xs text-stone-400">({counts[key]})</span>
          </button>
        ))}
      </div>

      {/* Dokumentenliste */}
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-stone-600">
                <tr>
                  <th className="text-left font-medium px-4 py-3 w-32">Nummer</th>
                  <th className="text-left font-medium px-4 py-3">Titel</th>
                  <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Bereich</th>
                  <th className="text-left font-medium px-4 py-3 hidden lg:table-cell w-20">VA</th>
                  <th className="text-left font-medium px-4 py-3 hidden lg:table-cell w-28">Stand</th>
                  <th className="text-left font-medium px-4 py-3 w-44">Status</th>
                  <th className="text-right font-medium px-4 py-3 w-32">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-stone-400">
                      Keine Dokumente gefunden.
                    </td>
                  </tr>
                )}
                {filtered.map((d) => {
                  const uploaded = uploadedDocs[d.nr];
                  const effectiveBadge = uploaded ? STATUS_BADGE.uploaded : (STATUS_BADGE[d.status] || FALLBACK_BADGE);
                  const downloadable = d.status === "available";
                  return (
                    <tr
                      key={d.nr}
                      className="border-b border-stone-100 hover:bg-stone-50/60 transition-colors"
                      data-testid={`azav-doc-row-${d.nr}`}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-stone-700">{d.nr}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-stone-900">{d.titel}</div>
                        {d.hinweis && (
                          <div className="text-xs text-stone-500 mt-0.5">{d.hinweis}</div>
                        )}
                        {uploaded && (
                          <div className="text-xs text-violet-600 mt-1">
                            Hochgeladen: {uploaded.filename} · {new Date(uploaded.uploaded_at).toLocaleDateString("de-DE")}
                          </div>
                        )}
                        {Array.isArray(referencesMap[d.nr]) && referencesMap[d.nr].length > 0 && (
                          <button
                            type="button"
                            onClick={() => setRefsModalCode(d.nr)}
                            className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100 transition"
                            data-testid={`azav-refs-badge-${d.nr}`}
                            title="Audit-Referenzen anzeigen"
                          >
                            <Paperclip size={11} /> {referencesMap[d.nr].length} Referenz{referencesMap[d.nr].length === 1 ? "" : "en"}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-stone-500 hidden md:table-cell">{d.bereich}</td>
                      <td className="px-4 py-3 text-stone-500 hidden lg:table-cell font-mono text-xs">{d.va}</td>
                      <td className="px-4 py-3 text-stone-500 hidden lg:table-cell text-xs">
                        {uploaded ? `${uploaded.version || d.version} · ${uploaded.stand || d.stand}` : `${d.version} · ${d.stand}`}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className={`${effectiveBadge.className} text-xs font-medium`}>
                          {effectiveBadge.label}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {/* Versteckter File-Input für Upload */}
                        <input
                          type="file"
                          accept="application/pdf"
                          ref={(el) => (fileInputRefs.current[d.nr] = el)}
                          className="hidden"
                          onChange={(e) => handleFileSelected(e, d)}
                          data-testid={`azav-file-input-${d.nr}`}
                        />
                        {downloadable ? (
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`azav-download-${d.nr}`}
                            onClick={() => downloadPDF(d.endpoint, d.filename)}
                          >
                            <Download size={14} className="mr-1.5" /> PDF
                          </Button>
                        ) : d.status === "per_teilnehmer" ? (
                          <span className="text-xs text-stone-400 inline-flex items-center gap-1">
                            <Users size={12} /> Teilnehmer-Akte
                          </span>
                        ) : uploaded ? (
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" data-testid={`azav-uploaded-download-${d.nr}`} onClick={() => downloadUploaded(d.nr)}>
                              <Download size={14} className="mr-1" /> PDF
                            </Button>
                            {!isReadOnly && (
                              <>
                                <Button size="sm" variant="ghost" title="Neue Version hochladen" data-testid={`azav-uploaded-replace-${d.nr}`} onClick={() => handleUploadClick(d.nr)} disabled={uploadingNr === d.nr}>
                                  <RefreshCw size={14} />
                                </Button>
                                <Button size="sm" variant="ghost" title="Löschen" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`azav-uploaded-delete-${d.nr}`} onClick={() => deleteUploaded(d.nr)}>
                                  <Trash2 size={14} />
                                </Button>
                              </>
                            )}
                          </div>
                        ) : isReadOnly ? (
                          <span className="text-xs text-stone-400">— kein Download —</span>
                        ) : (
                          <Button size="sm" variant="outline" className="text-violet-700 border-violet-200 hover:bg-violet-50" data-testid={`azav-upload-${d.nr}`} onClick={() => handleUploadClick(d.nr)} disabled={uploadingNr === d.nr}>
                            <Upload size={14} className="mr-1.5" /> {uploadingNr === d.nr ? "Lädt..." : "Hochladen"}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-amber-50/60 border-amber-200">
        <CardContent className="py-4">
          <p className="text-sm text-amber-900">
            <b>Hinweis:</b> Teilnehmerbezogene Dokumente (Erstgespräch-Checkliste, Laufzettel, Praktikumsbewertung, Anwesenheitsliste) werden in der jeweiligen <b>Teilnehmer-Akte</b> generiert, da sie individuelle Daten enthalten. Diese Liste hier zeigt den vollständigen QM-Dokumentenstand laut <code className="font-mono text-xs bg-white px-1 py-0.5 rounded border">NW_QM_03_Dokumentenliste_QM</code>.
          </p>
        </CardContent>
      </Card>

      {/* AZAV-Erklärung: Stundenplan vs. Anwesenheitsliste */}
      <Card className="bg-blue-50/60 border-blue-200" data-testid="azav-explainer-stundenplan-vs-anwesenheit">
        <CardContent className="py-5">
          <h3 className="text-base font-bold text-blue-900 mb-3 flex items-center gap-2">
            <FileText size={16} /> Unterschied: Stundenplan vs. Anwesenheitsliste (AZAV)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-blue-900">
              <thead>
                <tr className="border-b border-blue-200 text-left">
                  <th className="py-2 pr-3 font-semibold w-1/3">Aspekt</th>
                  <th className="py-2 pr-3 font-semibold w-1/3">Stundenplan</th>
                  <th className="py-2 font-semibold w-1/3">Anwesenheitsliste</th>
                </tr>
              </thead>
              <tbody className="align-top">
                <tr className="border-b border-blue-100">
                  <td className="py-2 pr-3 font-medium">QM-Code</td>
                  <td className="py-2 pr-3">
                    <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-blue-200">FB_TN_12</code> Behandlungspflege LG1 & LG2<br/>
                    <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-blue-200">FB_TN_13</code> Pflegehilfskraft & Betreuungsassistent<br/>
                    <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-blue-200">FB_TN_14</code> Ambulanter Pflegeassistent
                  </td>
                  <td className="py-2"><code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-blue-200">FB_TN_05_Anwesenheitsliste</code><br/><span className="text-xs text-stone-500">(maßnahmenübergreifend)</span></td>
                </tr>
                <tr className="border-b border-blue-100">
                  <td className="py-2 pr-3 font-medium">Zeitpunkt</td>
                  <td className="py-2 pr-3"><b>Vor</b> Maßnahmenbeginn erstellt (Planung)</td>
                  <td className="py-2"><b>Während/nach</b> Maßnahme dokumentiert (Ist-Zustand)</td>
                </tr>
                <tr className="border-b border-blue-100">
                  <td className="py-2 pr-3 font-medium">Inhalt</td>
                  <td className="py-2 pr-3">Tag · Datum · Uhrzeit · UE · Dozent · Thema/Lernziel</td>
                  <td className="py-2">Datum · Status (anwesend/entschuldigt/unentschuldigt) · UE · Dozent · Bemerkung</td>
                </tr>
                <tr className="border-b border-blue-100">
                  <td className="py-2 pr-3 font-medium">Dozent-Schreibweise</td>
                  <td className="py-2 pr-3"><b>Voller Name</b> (Audit-konform, keine Kürzel)</td>
                  <td className="py-2">Kürzel oder voller Name (Klassenbuch-konform)</td>
                </tr>
                <tr className="border-b border-blue-100">
                  <td className="py-2 pr-3 font-medium">Scope</td>
                  <td className="py-2 pr-3">Pro Kurs (1 PDF für alle Teilnehmer)</td>
                  <td className="py-2">Pro Teilnehmer (1 PDF je Teilnehmer)</td>
                </tr>
                <tr className="border-b border-blue-100">
                  <td className="py-2 pr-3 font-medium">Unterschrift</td>
                  <td className="py-2 pr-3">Schulleitung (1× am Ende)</td>
                  <td className="py-2">Dozent pro Tag + Statistik gesamt</td>
                </tr>
                <tr>
                  <td className="py-2 pr-3 font-medium">Wo erstellen?</td>
                  <td className="py-2 pr-3">
                    <span className="inline-block bg-white border border-blue-200 px-2 py-0.5 rounded text-xs">Klassenbuch</span> → Kurs öffnen → Button <b>„Stundenplan PDF"</b>
                  </td>
                  <td className="py-2">
                    <span className="inline-block bg-white border border-blue-200 px-2 py-0.5 rounded text-xs">Teilnehmer-Akte</span> → Tab <b>Anwesenheit</b> → „Anwesenheitsliste-PDF speichern"<br/>
                    <span className="text-xs text-stone-500">oder im Klassenbuch das kleine PDF-Icon je Teilnehmer</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs text-blue-800 mt-4 leading-relaxed">
            <b>AZAV-Audit-Tipp:</b> Beide Dokumenttypen werden bei der Prüfung verlangt, dienen aber unterschiedlichen Zwecken: Der <b>Stundenplan</b> (FB_TN_12 / FB_TN_13 / FB_TN_14 — je nach Maßnahme) belegt die <i>geplante</i> Durchführung der Maßnahme (Curriculum, Dozenten, UE-Verteilung). Die <b>Anwesenheitsliste (FB_TN_05)</b> belegt die <i>tatsächliche</i> Teilnahme jedes einzelnen Teilnehmers (Soll-Ist-Vergleich, AZAV-Quote ≥ 90%).
          </p>
          <p className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-3 leading-relaxed">
            <b>Wichtig:</b> Die neuen Codes <b>FB_TN_12, FB_TN_13, FB_TN_14</b> müssen noch in das offizielle QM-Handbuch (<code className="font-mono text-xs bg-white px-1 py-0.5 rounded border">NW_QM_03_Dokumentenliste_QM.pdf</code>) eingetragen werden, damit das CRM und die Dokumentenliste deckungsgleich sind.
          </p>
        </CardContent>
      </Card>

      {/* Audit-Referenzen Modal */}
      {refsModalCode && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setRefsModalCode(null)}
          data-testid="azav-refs-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3 bg-amber-50">
              <div>
                <div className="text-xs text-stone-500 font-mono">{refsModalCode}</div>
                <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                  <Paperclip size={16} className="text-amber-700" />
                  Audit-Referenzen ({(referencesMap[refsModalCode] || []).length})
                </h3>
              </div>
              <button
                onClick={() => setRefsModalCode(null)}
                className="p-1.5 rounded hover:bg-amber-100 text-stone-600"
                data-testid="azav-refs-modal-close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2">
              {(referencesMap[refsModalCode] || []).map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 p-3 border border-stone-200 rounded-md hover:bg-stone-50 transition"
                  data-testid={`azav-ref-${r.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-stone-900 text-sm truncate">{r.variant || "Standard"}</div>
                    <div className="text-xs text-stone-500 mt-0.5 flex flex-wrap gap-x-3">
                      <span className="font-mono">{r.filename}</span>
                      {r.size && <span>{(r.size/1024).toFixed(0)} KB</span>}
                      {r.uploaded_at && <span>{new Date(r.uploaded_at).toLocaleDateString("de-DE")}</span>}
                    </div>
                    {r.kommentar && (
                      <div className="text-xs text-stone-400 mt-1 italic">{r.kommentar}</div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => downloadReference(refsModalCode, r.id, r.filename)}
                    data-testid={`azav-ref-download-${r.id}`}
                  >
                    <Download size={14} className="mr-1.5" /> PDF
                  </Button>
                </div>
              ))}
            </div>
            <div className="border-t border-stone-200 px-5 py-2.5 bg-stone-50 text-xs text-stone-500">
              Diese Referenzen sind ausgefüllte Originale (Audit-Nachweise) und historische Vorlagen. Die im CRM aktive Live-Vorlage zeigt das Hauptdokument oben.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AZAVDokumente;
